import React, { useState } from "react";
import { X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { toast } from "sonner";

const Overlay = ({ children, onClose }) => (
  <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", backdropFilter: "blur(6px)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: "1.5rem" }}>
    <div onClick={(e) => e.stopPropagation()} className="card" style={{ background: "#111015", width: "100%", maxWidth: 440, padding: "2.5rem", position: "relative", borderColor: "rgba(201,162,75,0.2)" }}>
      {children}
    </div>
  </div>
);

export default function AuthModals() {
  const { authModal, setAuthModal, login, signup, forgotPassword } = useAuth();
  const { c } = useLang();
  const a = c.auth;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [role, setRole] = useState("");

  if (!authModal) return null;
  const close = () => setAuthModal(null);

  const CloseBtn = () => (
    <button onClick={close} style={{ position: "absolute", top: 18, right: 18, background: "none", border: "none", color: "var(--muted)", cursor: "pointer" }}><X size={20} /></button>
  );
  const Logo = () => (
    <div className="font-serif" style={{ fontSize: "1.5rem", textAlign: "center", marginBottom: "0.4rem", letterSpacing: "0.02em" }}>
      <span style={{ color: "var(--text)" }}>DSCR</span><span style={{ color: "var(--gold)" }}>Daily</span>
    </div>
  );
  const Social = () => {
    const googleLogin = () => {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUrl = window.location.origin + "/auth/callback";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };
    return (
      <div style={{ display: "grid", gap: "0.7rem", marginBottom: "1.4rem" }}>
        <button className="btn-dark" data-testid="google-login-btn" style={{ letterSpacing: "0.04em", textTransform: "none", fontSize: "0.85rem", justifyContent: "center" }} onClick={googleLogin}>{a.google}</button>
        <button className="btn-dark" style={{ letterSpacing: "0.04em", textTransform: "none", fontSize: "0.85rem", justifyContent: "center" }} onClick={() => toast(c.toast.social)}>{a.apple}</button>
      </div>
    );
  };
  const Divider = () => (
    <div style={{ display: "flex", alignItems: "center", gap: "1rem", margin: "0 0 1.4rem", color: "var(--muted-2)", fontSize: "0.72rem", letterSpacing: "0.1em" }}>
      <span style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.08)" }} />{a.or}<span style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.08)" }} />
    </div>
  );

  if (authModal === "signin") {
    return (
      <Overlay onClose={close}>
        <CloseBtn /><Logo />
        <p className="body-text" style={{ textAlign: "center", marginBottom: "1.6rem", fontSize: "0.9rem" }}>{a.welcomeBack}</p>
        <Social /><Divider />
        <div style={{ display: "grid", gap: "0.9rem" }}>
          <input className="field" data-testid="signin-email" placeholder={a.email} value={email} onChange={(e) => setEmail(e.target.value)} />
          <input className="field" data-testid="signin-password" type="password" placeholder={a.password} value={password} onChange={(e) => setPassword(e.target.value)} />
          <div style={{ textAlign: "right" }}>
            <button onClick={() => setAuthModal("reset")} style={{ background: "none", border: "none", color: "var(--gold)", fontSize: "0.78rem", cursor: "pointer" }}>{a.forgot}</button>
          </div>
          <button className="btn-gold" data-testid="signin-submit" style={{ width: "100%" }} onClick={async () => { try { await login(email, password); toast.success(c.toast.signedIn); } catch (e) { toast.error(e?.response?.data?.detail || c.toast.signInErr); } }}>{a.signIn}</button>
        </div>
        <p style={{ textAlign: "center", marginTop: "1.4rem", fontSize: "0.82rem", color: "var(--muted)" }}>{a.noAccount} <button onClick={() => setAuthModal("signup")} style={{ background: "none", border: "none", color: "var(--gold)", cursor: "pointer" }}>{a.createOne}</button></p>
      </Overlay>
    );
  }

  if (authModal === "signup") {
    return (
      <Overlay onClose={close}>
        <CloseBtn /><Logo />
        <p className="body-text" style={{ textAlign: "center", marginBottom: "1.6rem", fontSize: "0.9rem" }}>{a.joinPara}</p>
        <Social /><Divider />
        <div style={{ display: "grid", gap: "0.9rem" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.7rem" }}>
            <input className="field" placeholder={a.firstName} value={first} onChange={(e) => setFirst(e.target.value)} />
            <input className="field" placeholder={a.lastName} value={last} onChange={(e) => setLast(e.target.value)} />
          </div>
          <input className="field" placeholder={a.email} value={email} onChange={(e) => setEmail(e.target.value)} />
          <input className="field" type="password" placeholder={a.password} value={password} onChange={(e) => setPassword(e.target.value)} />
          <select className="field" value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="">{a.iam}</option>
            {c.contact.roles.map((r) => <option key={r}>{r}</option>)}
          </select>
          <button className="btn-gold" style={{ width: "100%" }} onClick={async () => { if (!email || !password) { toast.error(c.toast.emailReq); return; } try { await signup({ firstName: first, lastName: last, email, password, role }); toast.success(c.toast.created); } catch (e) { toast.error(e?.response?.data?.detail || c.toast.createErr); } }}>{a.createAccount}</button>
        </div>
        <p style={{ textAlign: "center", marginTop: "1.4rem", fontSize: "0.82rem", color: "var(--muted)" }}>{a.hasAccount} <button onClick={() => setAuthModal("signin")} style={{ background: "none", border: "none", color: "var(--gold)", cursor: "pointer" }}>{a.signInLink}</button></p>
      </Overlay>
    );
  }

  return (
    <Overlay onClose={close}>
      <CloseBtn /><Logo />
      <h3 className="font-serif" style={{ fontSize: "1.5rem", textAlign: "center", margin: "0.6rem 0" }}>{a.resetTitle}</h3>
      <p className="body-text" style={{ textAlign: "center", marginBottom: "1.6rem", fontSize: "0.9rem" }}>{a.resetPara}</p>
      <div style={{ display: "grid", gap: "0.9rem" }}>
        <input className="field" data-testid="forgot-email-input" placeholder={a.email} value={email} onChange={(e) => setEmail(e.target.value)} />
        <button className="btn-gold" data-testid="send-reset-btn" style={{ width: "100%" }} onClick={async () => { if (!email || !email.includes("@")) { toast.error(c.toast.emailReq); return; } try { await forgotPassword(email); toast.success(c.toast.resetSent); setAuthModal("signin"); } catch (e) { toast.error(e?.response?.data?.detail || c.toast.signInErr); } }}>{a.sendReset}</button>
      </div>
      <p style={{ textAlign: "center", marginTop: "1.4rem", fontSize: "0.82rem", color: "var(--muted)" }}><button onClick={() => setAuthModal("signin")} style={{ background: "none", border: "none", color: "var(--gold)", cursor: "pointer" }}>{a.backSignIn}</button></p>
    </Overlay>
  );
}
